import React from "react";
import SectionCard from "../components/SectionCard";
import {
  BilingualTableHeader,
  BilingualTextRow,
  BilingualImageRow,
} from "../components/BilingualFormFields";
import ReadOnlyBadge from "../components/ReadOnlyBadge";
import { getError } from "../utils/validation";
import { countSectionErrors } from "./SharedCmsSections";

function ContactInfoSection({
  contactEn,
  contactEs,
  setContactEn,
  setContactEs,
  errors,
  onImageUpload,
  uploadingKey,
}) {
  const cardsEn = contactEn.infoCards || [];
  const cardsEs = contactEs.infoCards || [];

  const updateEn = (index, patch) => {
    const next = cardsEn.map((card, idx) => (idx === index ? { ...card, ...patch } : card));
    setContactEn({ ...contactEn, infoCards: next });
  };
  const updateEs = (index, patch) => {
    const next = cardsEs.map((card, idx) => (idx === index ? { ...card, ...patch } : card));
    setContactEs({ ...contactEs, infoCards: next });
  };

  return (
    <SectionCard
      id="infoCards"
      title="Contact info cards"
      errorCount={countSectionErrors(errors, ["infoCards."])}
      collapsible
    >
      {cardsEn.map((card, index) => (
        <div key={card.id} className="cms-bilingual-block">
          <ReadOnlyBadge id={card.id} />
          <BilingualTextRow
            label="Title"
            fieldKey={`infoCards.${index}.title`}
            valueEn={card.title}
            valueEs={(cardsEs[index] && cardsEs[index].title) || ""}
            onChangeEn={(v) => updateEn(index, { title: v })}
            onChangeEs={(v) => updateEs(index, { title: v })}
            errorEn={getError(`infoCards.${index}.title`, errors, "en")}
            errorEs={getError(`infoCards.${index}.title`, errors, "es")}
          />
          <BilingualTextRow
            label="Value"
            fieldKey={`infoCards.${index}.value`}
            valueEn={card.value}
            valueEs={(cardsEs[index] && cardsEs[index].value) || ""}
            onChangeEn={(v) => updateEn(index, { value: v })}
            onChangeEs={(v) => updateEs(index, { value: v })}
            errorEn={getError(`infoCards.${index}.value`, errors, "en")}
            errorEs={getError(`infoCards.${index}.value`, errors, "es")}
          />
          <BilingualTextRow
            label="Detail"
            fieldKey={`infoCards.${index}.detail`}
            valueEn={card.detail}
            valueEs={(cardsEs[index] && cardsEs[index].detail) || ""}
            onChangeEn={(v) => updateEn(index, { detail: v })}
            onChangeEs={(v) => updateEs(index, { detail: v })}
            errorEn={getError(`infoCards.${index}.detail`, errors, "en")}
            errorEs={getError(`infoCards.${index}.detail`, errors, "es")}
          />
          <BilingualImageRow
            label="Icon"
            fieldKey={`infoCards.${index}.icon`}
            srcEn={card.icon}
            altEn=""
            srcEs={(cardsEs[index] && cardsEs[index].icon) || ""}
            altEs=""
            onSrcEn={(v) => updateEn(index, { icon: v })}
            onAltEn={() => {}}
            onSrcEs={(v) => updateEs(index, { icon: v })}
            onAltEs={() => {}}
            onUploadEn={(f) =>
              onImageUpload("en", `info.${index}`, f, (url) => updateEn(index, { icon: url }))
            }
            onUploadEs={(f) =>
              onImageUpload("es", `info.${index}`, f, (url) => updateEs(index, { icon: url }))
            }
            srcErrorEn={getError(`infoCards.${index}.icon`, errors, "en")}
            srcErrorEs={getError(`infoCards.${index}.icon`, errors, "es")}
            uploadingEn={uploadingKey === `en.info.${index}`}
            uploadingEs={uploadingKey === `es.info.${index}`}
            showAlt={false}
          />
        </div>
      ))}
    </SectionCard>
  );
}

function ContactMessageSection({
  contactEn,
  contactEs,
  setContactEn,
  setContactEs,
  errors,
  onImageUpload,
  uploadingKey,
}) {
  const msgEn = contactEn.messageSection || {};
  const msgEs = contactEs.messageSection || {};
  const fieldsEn = (msgEn.form && msgEn.form.fields) || [];
  const fieldsEs = (msgEs.form && msgEs.form.fields) || [];

  const setMsgEn = (patch) =>
    setContactEn({ ...contactEn, messageSection: { ...msgEn, ...patch } });
  const setMsgEs = (patch) =>
    setContactEs({ ...contactEs, messageSection: { ...msgEs, ...patch } });

  const updateFieldEn = (index, patch) => {
    const next = fieldsEn.map((field, idx) => (idx === index ? { ...field, ...patch } : field));
    setMsgEn({ form: { ...msgEn.form, fields: next } });
  };
  const updateFieldEs = (index, patch) => {
    const next = fieldsEs.map((field, idx) => (idx === index ? { ...field, ...patch } : field));
    setMsgEs({ form: { ...msgEs.form, fields: next } });
  };

  return (
    <SectionCard
      id="messageSection"
      title="Message form"
      errorCount={countSectionErrors(errors, ["messageSection."])}
      collapsible
    >
      <BilingualTextRow
        label="Section title"
        fieldKey="messageSection.title"
        valueEn={msgEn.title}
        valueEs={msgEs.title}
        onChangeEn={(v) => setMsgEn({ title: v })}
        onChangeEs={(v) => setMsgEs({ title: v })}
        errorEn={getError("messageSection.title", errors, "en")}
        errorEs={getError("messageSection.title", errors, "es")}
      />
      <BilingualTextRow
        label="Section description"
        fieldKey="messageSection.description"
        valueEn={msgEn.description}
        valueEs={msgEs.description}
        onChangeEn={(v) => setMsgEn({ description: v })}
        onChangeEs={(v) => setMsgEs({ description: v })}
        multiline
        rows={2}
        errorEn={getError("messageSection.description", errors, "en")}
        errorEs={getError("messageSection.description", errors, "es")}
      />
      <BilingualImageRow
        label="Side image"
        fieldKey="messageSection.image"
        srcEn={msgEn.image && msgEn.image.src}
        altEn={msgEn.image && msgEn.image.alt}
        srcEs={msgEs.image && msgEs.image.src}
        altEs={msgEs.image && msgEs.image.alt}
        onSrcEn={(v) => setMsgEn({ image: { ...msgEn.image, src: v } })}
        onAltEn={(v) => setMsgEn({ image: { ...msgEn.image, alt: v } })}
        onSrcEs={(v) => setMsgEs({ image: { ...msgEs.image, src: v } })}
        onAltEs={(v) => setMsgEs({ image: { ...msgEs.image, alt: v } })}
        onUploadEn={(f) =>
          onImageUpload("en", "msg.img", f, (url) => setMsgEn({ image: { ...msgEn.image, src: url } }))
        }
        onUploadEs={(f) =>
          onImageUpload("es", "msg.img", f, (url) => setMsgEs({ image: { ...msgEs.image, src: url } }))
        }
        srcErrorEn={getError("messageSection.image.src", errors, "en")}
        altErrorEn={getError("messageSection.image.alt", errors, "en")}
        srcErrorEs={getError("messageSection.image.src", errors, "es")}
        altErrorEs={getError("messageSection.image.alt", errors, "es")}
        uploadingEn={uploadingKey === "en.msg.img"}
        uploadingEs={uploadingKey === "es.msg.img"}
      />
      <BilingualTextRow
        label="Submit button"
        fieldKey="messageSection.form.submitLabel"
        valueEn={msgEn.form && msgEn.form.submitLabel}
        valueEs={msgEs.form && msgEs.form.submitLabel}
        onChangeEn={(v) => setMsgEn({ form: { ...msgEn.form, submitLabel: v } })}
        onChangeEs={(v) => setMsgEs({ form: { ...msgEs.form, submitLabel: v } })}
        errorEn={getError("messageSection.form.submitLabel", errors, "en")}
        errorEs={getError("messageSection.form.submitLabel", errors, "es")}
      />
      {fieldsEn.map((field, index) => (
        <div key={field.id} className="cms-bilingual-block">
          <ReadOnlyBadge id={field.id} />
          <BilingualTextRow
            label="Label"
            fieldKey={`messageSection.form.fields.${index}.label`}
            valueEn={field.label}
            valueEs={(fieldsEs[index] && fieldsEs[index].label) || ""}
            onChangeEn={(v) => updateFieldEn(index, { label: v })}
            onChangeEs={(v) => updateFieldEs(index, { label: v })}
            errorEn={getError(`messageSection.form.fields.${index}.label`, errors, "en")}
            errorEs={getError(`messageSection.form.fields.${index}.label`, errors, "es")}
          />
          <BilingualTextRow
            label="Placeholder"
            fieldKey={`messageSection.form.fields.${index}.placeholder`}
            valueEn={field.placeholder}
            valueEs={(fieldsEs[index] && fieldsEs[index].placeholder) || ""}
            onChangeEn={(v) => updateFieldEn(index, { placeholder: v })}
            onChangeEs={(v) => updateFieldEs(index, { placeholder: v })}
            errorEn={getError(`messageSection.form.fields.${index}.placeholder`, errors, "en")}
            errorEs={getError(`messageSection.form.fields.${index}.placeholder`, errors, "es")}
          />
        </div>
      ))}
    </SectionCard>
  );
}

export { ContactInfoSection, ContactMessageSection };
